import styled from 'styled-components';

import Drawer from '@mui/material/Drawer';

import { colors } from 'theme';

import { Button } from 'components/Button';

import { typographyVariant } from 'core/styles';

export const OptionsDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 480px;
    max-width: calc(100vw - 32px);
    border-left: 1px solid ${colors.grey[300]};
    display: flex;
    flex-direction: column;
    box-shadow: ${colors.shadows.card};
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.divider};
`;

export const DrawerTitle = styled.h2`
  ${typographyVariant('h6')};
  margin: 0;
  color: ${colors.text.primary};
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DrawerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid ${colors.divider};
  background: ${colors.common.white};
`;

export const InlineActionButton = styled(Button)`
  width: fit-content;
`;

/** Explicit px, not flex: a flex item cannot shrink past min-content, so the headings would drift. */
const optionTracks = (scored?: boolean): string =>
  scored ? '24px minmax(0, 1fr) 68px 44px 32px' : '24px minmax(0, 1fr) 44px 32px';

export const OptionRow = styled.div<{ $scored?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $scored }) => optionTracks($scored)};
  column-gap: 8px;
  align-items: center;
  min-height: 44px;
`;

export const OptionListHeader = styled.div<{ $scored?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $scored }) => optionTracks($scored)};
  column-gap: 8px;
  align-items: center;
  padding-bottom: 4px;
`;

export const OptionDragHandle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.text.disabled};
  cursor: grab;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${colors.primary.main};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const OptionLabelCol = styled.div`
  min-width: 0;
`;

export const OptionScoreCol = styled.div`
  min-width: 0;
`;

export const OptionColourCol = styled.div`
  min-width: 0;
  display: flex;
  justify-content: center;
`;

export const OptionActionCol = styled.div`
  min-width: 0;
  display: flex;
  justify-content: center;
`;

export const OptionColHead = styled.span<{ $center?: boolean }>`
  display: block;
  ${typographyVariant('caption')};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${colors.text.secondary};
  text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

/** An outlined ring, so "no colour" does not read as grey. */
export const ColourSwatch = styled.button<{ $bg: string; $empty?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: ${({ $empty }) => ($empty ? `1px dashed ${colors.grey[400]}` : `1px solid ${colors.grey[300]}`)};
  background: ${({ $bg, $empty }) => ($empty ? colors.common.white : $bg)};
  cursor: pointer;
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${colors.primary.main};
    outline-offset: 2px;
  }
`;

export const SwatchDot = styled.span<{ $bg: string; $empty?: boolean }>`
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-right: 12px;
  border: ${({ $empty }) => ($empty ? `1px dashed ${colors.grey[400]}` : `1px solid ${colors.grey[300]}`)};
  background: ${({ $bg, $empty }) => ($empty ? colors.common.white : $bg)};
`;

export const CommitError = styled.p`
  ${typographyVariant('body2')};
  color: ${colors.error.main};
  margin: 0;
`;

export const DrawerNote = styled.p`
  ${typographyVariant('body2')};
  color: ${colors.text.secondary};
  margin: 0;
`;

export const FixedOptionsNote = styled(DrawerNote)`
  padding: 8px 12px;
  border-radius: 8px;
  background: ${colors.background.grey};
`;

export const ExplainerCard = styled.div`
  border: 1px solid ${colors.grey[200]};
  border-radius: 8px;
  padding: 12px;
  background: ${colors.background.grey};
`;

export const ExplainerHeading = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  ${typographyVariant('body2')};
  font-weight: 600;
  color: ${colors.text.primary};
`;

export const ExplainerBody = styled.div`
  ${typographyVariant('caption')};
  color: ${colors.text.secondary};
`;

export const ExplainerList = styled.ul`
  margin: 0;
  padding-left: 18px;

  li + li {
    margin-top: 4px;
  }
`;
